import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Card, CardContent, Typography, Stack, Chip, Button, CircularProgress,
  Alert, List, ListItem, ListItemText, IconButton, Tooltip, Menu, MenuItem, Collapse,
} from '@mui/material';
import { Sync, LinkOff, AddLink, ArrowDropDown } from '@mui/icons-material';
import { httpClient } from '../services/http-client';
import { InstitutionBranding, primeBranding } from '../services/institutions-service';
import { InstitutionMark } from '../components/InstitutionMark';
import { useAuthStore } from '../stores/auth-store';
import { needsReauth } from '../utils/plaid-status';
import {
  accountCountLabel, actionLabel, connectionStatusView, severityColor, viewerMessage, viewerTitle,
} from '../utils/connection-messages';
import { addedAccountsMessage, refreshAndSync } from '../utils/plaid-reconnect';
import { MapAccountsDialog, type AccountMapping, type MappingChoice } from '../components/connections/MapAccountsDialog';
import { MergeAccountsDialog, type MergeableAccount, type MergeResult } from '../components/connections/MergeAccountsDialog';

interface Connection {
  id: string;
  plaid_item_id: string;
  institution_name?: string | null;
  status: string;
  credential_set?: string;
  /** Linked FolioWorth accounts this connection feeds. */
  account_count?: number;
  last_synced_at?: string | null;
  last_error?: string | null;
  /** Plaid's error_code from the last failure, e.g. ITEM_LOGIN_REQUIRED. */
  last_error_code?: string | null;
  last_error_at?: string | null;
  /** When the bank's consent lapses (from Plaid's PENDING_EXPIRATION webhook). */
  consent_expires_at?: string | null;
  /** Set when Plaid reports accounts at the bank this connection does not share yet. */
  new_accounts_available_at?: string | null;
  /** Set when the holdings call failed (migration 104). */
  investments_unavailable_at?: string | null;
  /** Plaid's code for that failure; PRODUCTS_NOT_SUPPORTED needs no action. */
  investments_unavailable_code?: string | null;
  created_at: string;
  /** Who linked it. Disconnecting is restricted to this member. */
  owner_user_id?: string;
  owner_name?: string;
  owner_email?: string;
}

interface CredentialSet {
  name: string; label: string;
  /** 0 = no FolioWorth limit (a household's own Plaid keys, #42). */
  item_limit: number; used: number; remaining: number | null;
  own_keys?: boolean;
  /** True when used/remaining count every household (Plaid limits Items per client). */
  global?: boolean;
  used_by_household?: number;
}

interface ConnectionsResponse {
  connections: Connection[];
  configured: boolean;
  credential_sets?: CredentialSet[];
  total_capacity?: number;
  /** Branding for each linked institution, keyed by institution_name. */
  branding?: Record<string, InstitutionBranding>;
}

const PLAID_SCRIPT = 'https://cdn.plaid.com/link/v2/stable/link-initialize.js';

/**
 * Load the Plaid Link SDK on demand.
 *
 * Loaded lazily rather than in index.html so the script is only fetched by
 * users who actually connect a bank, and so a CDN outage cannot block app boot.
 */
const loadPlaidScript = (): Promise<void> =>
  new Promise((resolve, reject) => {
    if ((window as any).Plaid) return resolve();
    const existing = document.querySelector(`script[src="${PLAID_SCRIPT}"]`);
    if (existing) {
      existing.addEventListener('load', () => resolve());
      existing.addEventListener('error', () => reject(new Error('Plaid SDK failed to load')));
      return;
    }
    const s = document.createElement('script');
    s.src = PLAID_SCRIPT;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error('Plaid SDK failed to load'));
    document.head.appendChild(s);
  });

/**
 * Bank connections via Plaid.
 *
 * Read-only ingestion: connecting pulls transactions and balances. Nothing on
 * this page can move money.
 */
export const ConnectionsPage: React.FC = () => {
  const currentUserId = useAuthStore((s) => s.user?.id);

  /**
   * Whether the signed-in member linked this institution.
   *
   * Defaults to FALSE when either id is missing. An unknown owner must not
   * grant the destructive action -- the server would reject it anyway, but a
   * button that looks available and then fails is worse than one that is
   * plainly disabled.
   */
  const isOwner = (c: Connection) =>
    Boolean(currentUserId && c.owner_user_id && c.owner_user_id === currentUserId);

  /** A human label for the owner, falling back through what the API gives us. */
  const ownerLabel = (c: Connection) =>
    c.owner_name?.trim() || c.owner_email?.trim() || 'the member who linked it';

  /** The owner's name for "ask Tia to …"; null when the API names nobody. */
  const ownerName = (c: Connection) => c.owner_name?.trim() || c.owner_email?.trim() || null;

  /** Friendly status for a connection, from the message catalog. */
  const view = connectionStatusView;

  const [data, setData] = useState<ConnectionsResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [syncing, setSyncing] = useState<string | null>(null);
  const [reconnecting, setReconnecting] = useState<string | null>(null);
  const [connectMenu, setConnectMenu] = useState<HTMLElement | null>(null);
  const [openDetails, setOpenDetails] = useState<Record<string, boolean>>({});
  // After Link: map bank accounts onto existing manual accounts (#41).
  const [mapping, setMapping] = useState<AccountMapping | null>(null);
  const [mergeOpen, setMergeOpen] = useState(false);
  const [mergeAccounts, setMergeAccounts] = useState<MergeableAccount[]>([]);
  // The expiry / needs-re-auth email links here with ?reconnect=<id>. Read
  // once: Plaid Link cannot be opened without a click, so the page points at
  // the connection and offers the button rather than launching it.
  const [reconnectTarget] = useState<string | null>(() => {
    try {
      return new URLSearchParams(window.location.search).get('reconnect');
    } catch {
      return null;
    }
  });

  const load = useCallback(() => {
    httpClient.get<ConnectionsResponse>('/plaid/connections')
      .then((res) => {
        // The endpoint already resolved branding for these institutions;
        // seeding the cache means the marks draw on first paint and no other
        // page has to look the same names up again.
        primeBranding(res.branding);
        setData(res);
      })
      .catch((e) => setError(e?.message ?? 'Failed to load connections'));
  }, []);

  useEffect(load, [load]);

  const connect = async (credentialSet?: string) => {
    setBusy(true);
    setNotice(null);
    try {
      await loadPlaidScript();
      const qs = credentialSet ? `?credential_set=${encodeURIComponent(credentialSet)}` : '';
      const { link_token, credential_set } = await httpClient.post<{
        link_token: string; credential_set: string;
      }>(`/plaid/link-token${qs}`, {});

      const handler = (window as any).Plaid.create({
        token: link_token,
        onSuccess: async (publicToken: string, metadata: any) => {
          try {
            const res = await httpClient.post<{ linked: number; mapping_required?: boolean; mapping?: AccountMapping }>('/plaid/exchange', {
              public_token: publicToken,
              institution_id: metadata?.institution?.institution_id ?? '',
              institution_name: metadata?.institution?.name ?? '',
              // Must match the set that minted the link token: the resulting
              // access token is only valid for that client_id.
              credential_set: credential_set,
            });
            if (res.mapping_required && res.mapping) {
              // Some of these accounts may already be kept here by hand:
              // confirm before anything is linked.
              setMapping(res.mapping);
              load();
              return;
            }
            setNotice(`Connected — ${res.linked} account(s) linked. Run a sync to pull transactions.`);
            load();
          } catch (e: any) {
            setNotice(e?.message ?? 'Failed to complete connection');
          }
        },
        onExit: (err: any) => {
          if (err) setNotice(err.display_message || err.error_message || 'Link cancelled');
        },
      });
      handler.open();
    } catch (e: any) {
      setNotice(e?.message ?? 'Failed to start Plaid Link');
    } finally {
      setBusy(false);
    }
  };

  const finishSetup = async (id: string) => {
    setNotice(null);
    try {
      setMapping(await httpClient.get<AccountMapping>(`/plaid/connections/${id}/mapping`));
    } catch (e: any) {
      setNotice(e?.message ?? 'Failed to load the bank accounts');
    }
  };

  const saveMapping = async (mappings: MappingChoice[]) => {
    if (!mapping) return;
    const res = await httpClient.post<{ linked: number; attached: number; created: number }>(
      `/plaid/connections/${mapping.connection_id}/mapping`, { mappings });
    setMapping(null);
    setNotice(`Connected — ${res.attached} account(s) attached to existing accounts, ${res.created} created. Run a sync to pull transactions.`);
    load();
  };

  const openMerge = async () => {
    try {
      const raw: any = await httpClient.get<any>('/accounts');
      const list: any[] = raw?.data ?? raw?.accounts ?? raw ?? [];
      setMergeAccounts(list.filter((a) => ['Asset', 'Debt', 'Investment'].includes(a.account_type ?? a.type))
        .map((a) => ({ id: a.id, name: a.display_name || a.name, type: a.account_type ?? a.type, currency: a.currency })));
      setMergeOpen(true);
    } catch (e: any) {
      setNotice(e?.message ?? 'Failed to load accounts');
    }
  };

  const merge = (fromId: string, intoId: string) =>
    httpClient.post<MergeResult>('/accounts/merge', { from_account_id: fromId, into_account_id: intoId })
      .then((r) => { load(); return r; });

  const sync = async (id: string) => {
    setSyncing(id);
    setNotice(null);
    try {
      const res = await httpClient.post<{
        posted: number; skipped: number; unmapped_plaid_accounts: string[];
      }>(`/plaid/connections/${id}/sync`, {});
      const extra = res.unmapped_plaid_accounts?.length
        ? ` ${res.unmapped_plaid_accounts.length} account(s) were not linked and were skipped.`
        : '';
      setNotice(`Synced: ${res.posted} entries posted, ${res.skipped} skipped.${extra}`);
      load();
    } catch (e: any) {
      // The server now names the Plaid error and has already recorded it on
      // the connection, so reload: a needs-re-auth failure should flip the
      // badge immediately, not on the next visit.
      setNotice(e?.message ?? 'Sync failed');
      load();
    } finally {
      setSyncing(null);
    }
  };

  /**
   * Re-authenticate an existing connection to widen its consent.
   *
   * Uses Plaid Link in UPDATE MODE, which re-authorises the SAME Item. No new
   * Item is consumed, and the connection keeps its sync cursor and account
   * links -- so previously imported history stays attached rather than being
   * stranded against accounts a fresh Item would not recognise.
   *
   * onSuccess deliberately does NOT call /plaid/exchange. Update mode returns a
   * public token for an Item we already hold; exchanging it would mint a second
   * access token for the same Item and leave the old row pointing at a
   * credential nobody uses.
   */
  const reconnect = async (id: string, repair = false) => {
    setReconnecting(id);
    setNotice(null);
    try {
      await loadPlaidScript();
      const { link_token } = await httpClient.post<{ link_token: string }>(
        `/plaid/connections/${id}/reconnect${repair ? '?mode=repair' : ''}`, {});

      const handler = (window as any).Plaid.create({
        token: link_token,
        onSuccess: async () => {
          setNotice(repair
            ? 'Signed in again. Syncing…'
            : 'Reconnected. Syncing to pull holdings, trades and dividends…');
          // Link any account the owner just ticked in Link's account picker,
          // then sync -- update mode never reaches /plaid/exchange, so without
          // the refresh a newly shared account is never imported (#22). A
          // successful sync is also what clears Needs re-auth server-side.
          const r = await refreshAndSync(
            (url, body) => httpClient.post(url, body), id);
          const added = addedAccountsMessage(r.linkedNew);
          if (r.synced) {
            setNotice([repair
              ? 'Connected — reconnected and synced. The connection is active again.'
              : 'Reconnected and synced. Investment data should now be available.', added]
              .filter(Boolean).join(' '));
          } else {
            setNotice([r.syncError ?? 'Reconnected. Run a sync to pull new transactions.', added]
              .filter(Boolean).join(' '));
          }
          load();
        },
        onExit: (err: any) => {
          if (err) setNotice(err.display_message || err.error_message || 'Reconnect cancelled');
        },
      });
      handler.open();
    } catch (e: any) {
      setNotice(e?.message ?? 'Failed to start reconnect');
    } finally {
      setReconnecting(null);
    }
  };

  const disconnect = async (id: string) => {
    try {
      await httpClient.delete(`/plaid/connections/${id}`);
      setNotice('Disconnected. Imported transactions were kept.');
      load();
    } catch (e: any) {
      setNotice(e?.message ?? 'Failed to disconnect');
    }
  };

  if (error) return <Typography color="error" sx={{ p: 3 }}>{error}</Typography>;
  if (!data) {
    return (
      <Box sx={{ display: 'grid', placeItems: 'center', minHeight: 300, gap: 2 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 2 }}>
        <Box>
          <Typography variant="overline" color="text.secondary">Data sources</Typography>
          <Typography variant="h3">Connections</Typography>
          <Typography variant="body2" color="text.secondary">
            Link a bank to import transactions automatically
          </Typography>
        </Box>
        {/*
          One "Connect a bank" button. Plaid credential sets are an
          implementation detail (each has its own Item limit), so their names
          and remaining capacity live in a menu / tooltip, not the label.
        */}
        {(() => {
          const sets = data.credential_sets ?? [];
          const capacity = (s: CredentialSet) => s.own_keys || !s.item_limit
            ? `your own Plaid keys · ${s.used} connected`
            : `${s.remaining} of ${s.item_limit} connection${s.item_limit === 1 ? '' : 's'} left` +
              (s.global ? ' across FolioWorth' : '');
          const full = (s: CredentialSet) => s.remaining != null && s.remaining <= 0;
          if (sets.length > 1) {
            return (
              <>
                <Button
                  variant="contained" startIcon={<AddLink />} endIcon={<ArrowDropDown />}
                  onClick={(e) => setConnectMenu(e.currentTarget)}
                  disabled={!data.configured || busy}
                  aria-haspopup="menu" aria-expanded={Boolean(connectMenu)}
                >
                  {busy ? 'Opening…' : 'Connect a bank'}
                </Button>
                <Menu anchorEl={connectMenu} open={Boolean(connectMenu)} onClose={() => setConnectMenu(null)}>
                  {sets.map((s) => (
                    <MenuItem
                      key={s.name} disabled={full(s)}
                      onClick={() => { setConnectMenu(null); connect(s.name); }}
                    >
                      <ListItemText primary={s.label} secondary={capacity(s)} />
                    </MenuItem>
                  ))}
                </Menu>
              </>
            );
          }
          return (
            <Tooltip title={sets.length === 1 ? capacity(sets[0]) : ''}>
              <span>
                <Button
                  variant="contained" startIcon={<AddLink />}
                  onClick={() => connect()}
                  disabled={!data.configured || busy || (sets.length === 1 && full(sets[0]))}
                >
                  {busy ? 'Opening…' : 'Connect a bank'}
                </Button>
              </span>
            </Tooltip>
          );
        })()}
      </Stack>

      <MapAccountsDialog open={Boolean(mapping)} mapping={mapping} onCancel={() => setMapping(null)} onConfirm={saveMapping} />
      <MergeAccountsDialog open={mergeOpen} accounts={mergeAccounts} onClose={() => setMergeOpen(false)} onMerge={merge} />
      <Stack direction="row" justifyContent="flex-end" sx={{ mb: 1 }}>
        <Button size="small" onClick={() => void openMerge()}>Merge duplicate accounts</Button>
      </Stack>

      <Alert severity="info" sx={{ mb: 2 }}>
        Connections are <strong>read-only</strong>. FolioWorth imports transactions and
        balances; it cannot move money, make payments, or initiate transfers.
      </Alert>

      {!data.configured && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Plaid isn&apos;t configured yet. Add <code>PLAID_CLIENT_ID</code>,{' '}
          <code>PLAID_SECRET</code> and <code>PLAID_TOKEN_KEY</code> to{' '}
          <code>backend/.env</code> and restart the server. Start with{' '}
          <code>PLAID_ENV=sandbox</code> — sandbox uses fake credentials
          (<code>user_good</code> / <code>pass_good</code>) and doesn&apos;t consume any of
          your production Items.
        </Alert>
      )}

      {(() => {
        const target = reconnectTarget
          ? data.connections.find((c) => c.id === reconnectTarget)
          : undefined;
        if (!target) return null;
        return (
          <Alert
            severity="warning" sx={{ mb: 2 }}
            action={isOwner(target) ? (
              <Button
                color="inherit" size="small"
                onClick={() => reconnect(target.id, needsReauth(target) || Boolean(target.consent_expires_at))}
                disabled={reconnecting === target.id}
              >
                Sign in again
              </Button>
            ) : undefined}
          >
            {target.institution_name || 'This connection'}{' '}
            {needsReauth(target) ? 'needs you to sign in again.'
              : target.new_accounts_available_at && !target.consent_expires_at
                ? 'has new accounts — reconnect and tick them to add them.'
                : 'is due to be renewed.'}
            {!isOwner(target) && ` Only ${ownerLabel(target)} can do this — it needs their bank sign-in.`}
          </Alert>
        );
      })()}

      {notice && (
        <Alert
          severity={notice.startsWith('Synced') || notice.startsWith('Connected') ? 'success' : 'warning'}
          onClose={() => setNotice(null)}
          sx={{ mb: 2 }}
        >
          {notice}
        </Alert>
      )}

      <Card>
        <CardContent>
          <Typography variant="overline" color="text.secondary">
            Linked institutions ({data.connections.length})
          </Typography>

          {data.connections.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
              No banks connected yet.
            </Typography>
          ) : (
            <List dense>
              {data.connections.map((c) => (
                <ListItem
                  key={c.id}
                  sx={{ borderBottom: '1px solid var(--border-soft)' }}
                  secondaryAction={
                    <Stack direction="row" spacing={0.5}>
                      {(c.account_count ?? 0) === 0 && (
                        <Button size="small" variant="outlined" onClick={() => void finishSetup(c.id)}>
                          Finish setup
                        </Button>
                      )}
                      {/*
                        The accessible name sits on the BUTTON, and the tooltip
                        only describes it. Previously the Tooltip put
                        aria-label="Pull new transactions" on the wrapping
                        <span>, so anything that found the control by name --
                        a screen reader, a test, browser automation -- got the
                        span, and a click on the span never reaches the
                        button's onClick. Only a real pointer, which lands on
                        the icon inside the button, worked.
                      */}
                      <Tooltip title="Pull new transactions" describeChild>
                        <span>
                          <IconButton
                            size="small" onClick={() => sync(c.id)}
                            disabled={syncing === c.id}
                            aria-label="Pull new transactions"
                          >
                            {syncing === c.id
                              ? <CircularProgress size={16} />
                              : <Sync fontSize="small" />}
                          </IconButton>
                        </span>
                      </Tooltip>
                      {/*
                        Disconnecting is the owner's alone. Unlinking silently
                        stops THEIR imports and can only be undone by them,
                        through the bank's own consent flow. Syncing stays open
                        to everyone: refreshing data the household already has
                        costs nothing.

                        Rendered disabled rather than hidden, with a tooltip
                        naming the owner. A control that vanishes for reasons
                        the user cannot see reads as a missing feature; one that
                        is visibly disabled and says why teaches the rule. The
                        server enforces it regardless -- this is only the
                        explanation.
                      */}
                      {/*
                        Reconnect widens consent on the SAME Item. Offered
                        beside Sync rather than buried, because a connection
                        that was linked without the investments product looks
                        completely healthy -- Active, syncing, no error -- while
                        returning no holdings, trades or dividends at all. The
                        only visible symptom is data that is never there.

                        OWNER ONLY, unlike Sync. Reconnecting means re-entering
                        the BANK's own credentials, so no other member could
                        finish the flow even if they started it -- they would be
                        handed a Plaid Link session for someone else's bank and
                        a dead end. It is also the owner's consent being
                        widened, which is theirs to grant.
                      */}
                      <Tooltip describeChild title={
                        !isOwner(c)
                          ? `Only ${ownerLabel(c)} can reconnect this — it needs their bank sign-in`
                          : view(c).action === 'signIn'
                            ? 'Reconnect — sign in to the bank again. History and account links are kept.'
                            : 'Reconnect — re-authorise to enable investments (holdings, trades, dividends)'
                      }>
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => reconnect(c.id, view(c).action === 'signIn')}
                            disabled={!isOwner(c) || reconnecting === c.id}
                            aria-label="Reconnect"
                          >
                            {reconnecting === c.id
                              ? <CircularProgress size={16} />
                              : <AddLink fontSize="small" />}
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip describeChild title={
                        isOwner(c)
                          ? 'Disconnect (imported data is kept)'
                          : `Only ${ownerLabel(c)} can disconnect this — you can still refresh it`
                      }>
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => disconnect(c.id)}
                            disabled={!isOwner(c)}
                            aria-label="Disconnect"
                          >
                            <LinkOff fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Stack>
                  }
                >
                  <ListItemText
                    primary={
                      <Stack direction="row" spacing={1} alignItems="center">
                        <InstitutionMark
                          name={c.institution_name || c.plaid_item_id}
                          size={24}
                        />
                        {/* Institution names run long; truncate rather than
                            wrap so the row keeps its height. */}
                        <Typography variant="body2" fontWeight={650} sx={{
                          minWidth: 0, whiteSpace: 'nowrap',
                          overflow: 'hidden', textOverflow: 'ellipsis',
                        }}>
                          {c.institution_name || c.plaid_item_id}
                        </Typography>
                        {/* How many accounts this bank feeds. The credential
                            set ("Account 1") meant nothing to anyone; it stays
                            under Details for support (issue #35). */}
                        {accountCountLabel(c.account_count) && (
                          <Chip size="small" variant="outlined" label={accountCountLabel(c.account_count)} />
                        )}
                        <Chip
                          size="small" label={viewerTitle(view(c), isOwner(c) ? null : ownerName(c))}
                          sx={{ color: severityColor(view(c).severity) }}
                        />
                        {/* The fix is the owner's and nobody else's (it needs
                            their bank sign-in), so the call to action is
                            offered only to them; everyone else sees the chip
                            and whose it is. Sign in again = repair mode; Allow
                            investments = update mode requesting the product. */}
                        {actionLabel(view(c).action) && isOwner(c) && (
                          <Button
                            size="small" variant="outlined"
                            onClick={() => reconnect(c.id, view(c).action === 'signIn')}
                            disabled={reconnecting === c.id}
                          >
                            {actionLabel(view(c).action)}
                          </Button>
                        )}
                        {/* Plaid reported accounts at the bank this Item does
                            not share (e.g. one opened after linking). Only the
                            owner can add them, by reconnecting and ticking them
                            in Link's account list. Repair mode: no new product
                            is requested, so the flow is just the picker. */}
                        {c.new_accounts_available_at && (
                          <Chip size="small" label="New accounts available"
                            sx={{ color: 'var(--warn-text)' }} />
                        )}
                        {c.new_accounts_available_at && isOwner(c) && view(c).action === 'none' && (
                          <Button
                            size="small" variant="outlined"
                            onClick={() => reconnect(c.id, true)}
                            disabled={reconnecting === c.id}
                          >
                            Add accounts
                          </Button>
                        )}
                      </Stack>
                    }
                    secondaryTypographyProps={{ component: 'div' }}
                    secondary={
                      <Box>
                        <Stack
                          direction="row"
                          spacing={1}
                          alignItems="center"
                          flexWrap="wrap"
                          // A real separator between the two facts, rather than a
                          // bullet baked into one of them -- which left a dangling
                          // "·" whenever the owner line did not render.
                          divider={<Typography variant="caption" color="text.secondary">·</Typography>}
                        >
                          {view(c).message
                            ? <Typography variant="caption" sx={{ color: severityColor(view(c).severity) }}>
                                {viewerMessage(view(c), isOwner(c) ? null : ownerName(c))}
                              </Typography>
                            : <Typography variant="caption" color="text.secondary">
                                {c.last_synced_at
                                  ? `Last synced ${new Date(c.last_synced_at).toLocaleString()}`
                                  : 'Never synced'}
                              </Typography>}
                          {/* Who linked it. In a shared household the list mixes
                              everyone's banks, and without this there is no way
                              to tell your own from your partner's.

                              Rendered only when the API actually names someone.
                              The generic fallback ("the member who linked it")
                              belongs in the disconnect tooltip, where it explains
                              a refusal -- as a standing label it is pure noise,
                              occupying the spot where a name should be and
                              telling the reader nothing they did not know. */}
                          {c.consent_expires_at && (
                            <Typography variant="caption" sx={{
                              color: new Date(c.consent_expires_at).getTime() - Date.now() < 14 * 86400000
                                ? 'var(--warn-text)' : 'text.secondary',
                            }}>
                              Consent expires {new Date(c.consent_expires_at).toLocaleDateString()}
                            </Typography>
                          )}
                          {(isOwner(c) || c.owner_name?.trim() || c.owner_email?.trim()) && (
                            <Typography variant="caption" color="text.secondary">
                              Managed by {isOwner(c) ? 'you' : (c.owner_name?.trim() || c.owner_email?.trim())}
                            </Typography>
                          )}
                          {/* The raw Plaid code and sentence, for support --
                              out of the way unless asked for. */}
                          {(view(c).code || view(c).rawMessage) && (
                            <Button
                              size="small" variant="text"
                              sx={{ minWidth: 0, p: 0, fontSize: 12, textTransform: 'none' }}
                              aria-expanded={Boolean(openDetails[c.id])}
                              onClick={() => setOpenDetails((d) => ({ ...d, [c.id]: !d[c.id] }))}
                            >
                              {openDetails[c.id] ? 'Hide details' : 'Details'}
                            </Button>
                          )}
                        </Stack>
                        <Collapse in={Boolean(openDetails[c.id])} unmountOnExit>
                          <Box component="span" sx={{ display: 'block', mt: 0.5, fontFamily: 'monospace', fontSize: 12 }}
                            color="text.secondary">
                            {view(c).code && <Box component="span" sx={{ display: 'block' }}>Code: {view(c).code}</Box>}
                            {view(c).rawMessage && <Box component="span" sx={{ display: 'block' }}>{view(c).rawMessage}</Box>}
                            {c.credential_set && c.credential_set !== 'default' && (
                              <Box component="span" sx={{ display: 'block' }}>Credential set: {c.credential_set}</Box>
                            )}
                          </Box>
                        </Collapse>
                      </Box>
                    }
                  />
                </ListItem>
              ))}
            </List>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};
